import { plateFont } from '../../utils/fonts';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Snackbar,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  List,
  ListItem,
  ListItemText,
  TextField,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  Close as CloseIcon,
  TireRepair as TireRepairIcon,
  Person as PersonIcon,
  DirectionsCar as DirectionsCarIcon,
  Send as SendIcon,
  Print as PrintIcon,
  WarningAmber as WarningAmberIcon,
  TaskAlt as TaskAltIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useUserStore } from 'store';
import ImagePickerButton from '../shared/ImagePickerButton';
import { getCsrfToken, formatDate } from '../../utils';
import TireHotelCardForm from './TireHotelCardForm';
import CustomerDetailsDialog from '../customers/CustomerDetailsDialog';
import VehicleDetailsDialog from '../vehicles/VehicleDetailsDialog';
import ConditionReportDialog from './ConditionReportDialog';
import printTireLabel from './tireLabel';
import NeedsReplacementChip from './NeedsReplacementChip';

const getStatusColor = (status) => {
  switch (status) {
    case 'stored': return 'success';
    case 'on_vehicle': return 'info';
    case 'disposed': return 'default';
    default: return 'default';
  }
};

const getTreadColor = (depth) => {
  if (depth == null) return 'text.secondary';
  const d = parseFloat(depth);
  if (d < 3) return 'error.main';
  if (d < 4) return 'warning.main';
  return 'success.main';
};

function TireHotelCardDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentOrganization } = useUserStore();

  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successSnackbar, setSuccessSnackbar] = useState(null);

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [conditionReportOpen, setConditionReportOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  // The card only points at the customer and the vehicle; a wrong name or a
  // mistyped plate is fixed on those records, so open them from here.
  const [customerDialogOpen, setCustomerDialogOpen] = useState(false);
  const [vehicleDialogOpen, setVehicleDialogOpen] = useState(false);
  // "Uusittava" is set and cleared here, not on the edit form: it is the set's condition,
  // separate from where the set is, and both directions are written to the history.
  const [flagDialogOpen, setFlagDialogOpen] = useState(false);
  const [flagReason, setFlagReason] = useState('');
  const [flagSaving, setFlagSaving] = useState(false);
  const [flagError, setFlagError] = useState(null);

  const fetchCard = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/api/tire-hotel-cards/${id}/`);
      setCard(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchCard();
  }, [fetchCard]);

  const handleDelete = async () => {
    try {
      const headers = {
        'X-CSRFToken': getCsrfToken(),
        'X-Organization-Id': currentOrganization.organization_id,
      };
      await axios.delete(`/api/tire-hotel-cards/${id}/`, { headers });
      navigate('/tire-hotel');
    } catch (err) {
      console.error('Failed to delete card:', err);
    }
    setDeleteConfirmOpen(false);
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('hotel_card', id);
      formData.append('image', file);

      const headers = {
        'X-CSRFToken': getCsrfToken(),
        'X-Organization-Id': currentOrganization.organization_id,
        'Content-Type': 'multipart/form-data',
      };
      await axios.post('/api/tire-hotel-photos/', formData, { headers });
      setSuccessSnackbar(t('tire_hotel.photo_uploaded'));
      fetchCard();
    } catch (err) {
      console.error('Failed to upload photo:', err);
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoDelete = async (photoId) => {
    try {
      const headers = {
        'X-CSRFToken': getCsrfToken(),
        'X-Organization-Id': currentOrganization.organization_id,
      };
      await axios.delete(`/api/tire-hotel-photos/${photoId}/`, { headers });
      setSuccessSnackbar(t('tire_hotel.photo_deleted'));
      fetchCard();
    } catch (err) {
      console.error('Failed to delete photo:', err);
    }
  };

  const handleFormSaved = () => {
    setSuccessSnackbar(t('tire_hotel.card_saved'));
    fetchCard();
  };

  const openFlagDialog = () => {
    setFlagReason('');
    setFlagError(null);
    setFlagDialogOpen(true);
  };

  const handleSaveFlag = async () => {
    const flagging = !card.needs_replacement;
    const reason = flagReason.trim();
    if (flagging && !reason) {
      setFlagError(t('tire_hotel.replacement_reason_required'));
      return;
    }
    setFlagSaving(true);
    setFlagError(null);
    try {
      const headers = {
        'X-CSRFToken': getCsrfToken(),
        'X-Organization-Id': currentOrganization.organization_id,
      };
      const response = await axios.post(
        `/api/tire-hotel-cards/${id}/replacement-flag/`,
        { needs_replacement: flagging, reason },
        { headers }
      );
      setCard(response.data);
      setFlagDialogOpen(false);
      setSuccessSnackbar(t(flagging ? 'tire_hotel.replacement_flag_saved' : 'tire_hotel.replacement_flag_cleared'));
    } catch (err) {
      console.error('Failed to save replacement flag:', err);
      setFlagError(t('tire_hotel.replacement_flag_error'));
    } finally {
      setFlagSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/tire-hotel')}
          sx={{ mt: 2 }}
        >
          {t('tire_hotel.back_to_cards')}
        </Button>
      </Box>
    );
  }

  if (!card) return null;

  const historyActionLabel = (action) => {
    const key = `tire_hotel.history_${action}`;
    const translated = t(key);
    return translated !== key ? translated : action;
  };

  return (
    <Box data-testid="tire-hotel-card-detail">
      {/* Snackbar */}
      <Snackbar
        open={!!successSnackbar}
        autoHideDuration={4000}
        onClose={() => setSuccessSnackbar(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccessSnackbar(null)} severity="success" sx={{ width: '100%' }}>
          {successSnackbar}
        </Alert>
      </Snackbar>

      {/* Photo lightbox */}
      <Dialog
        open={!!lightboxPhoto}
        onClose={() => setLightboxPhoto(null)}
        maxWidth="lg"
      >
        {lightboxPhoto && (
          <Box sx={{ position: 'relative' }}>
            <IconButton
              onClick={() => setLightboxPhoto(null)}
              sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.5)', color: 'white', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
            <img
              src={lightboxPhoto.image}
              alt={lightboxPhoto.description || ''}
              style={{ display: 'block', maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain' }}
            />
          </Box>
        )}
      </Dialog>

      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          underline="hover"
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/tire-hotel')}
        >
          {t('tire_hotel.menu_title')}
        </Link>
        <Link
          underline="hover"
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/tire-hotel')}
        >
          {t('tire_hotel.hotel_cards')}
        </Link>
        <Typography color="text.primary">{card.customer_name}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
              }}
            >
              {card.customer_name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ ...plateFont }}>
                {card.vehicle_registration_number}
              </Typography>
              <Chip
                size="small"
                label={t(`tire_hotel.${card.status}`)}
                color={getStatusColor(card.status)}
              />
              <NeedsReplacementChip card={card} t={t} />
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color={card.needs_replacement ? 'success' : 'warning'}
            startIcon={card.needs_replacement ? <TaskAltIcon /> : <WarningAmberIcon />}
            onClick={openFlagDialog}
            size="small"
            sx={{ borderRadius: '10px' }}
            data-testid="card-detail-replacement-flag"
          >
            {t(card.needs_replacement ? 'tire_hotel.clear_needs_replacement' : 'tire_hotel.mark_needs_replacement')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => printTireLabel(card, currentOrganization, t, true)}
            size="small"
            sx={{ borderRadius: '10px' }}
          >
            {t('tire_hotel.print_label_landscape')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => printTireLabel(card, currentOrganization, t, false)}
            size="small"
            sx={{ borderRadius: '10px' }}
          >
            {t('tire_hotel.print_label_portrait')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<SendIcon />}
            onClick={() => setConditionReportOpen(true)}
            size="small"
            sx={{ borderRadius: '10px' }}
            data-testid="card-detail-send-report"
          >
            {t('tire_hotel.send_condition_report')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => setEditDialogOpen(true)}
            size="small"
            sx={{ borderRadius: '10px' }}
            data-testid="card-detail-edit"
          >
            {t('Edit')}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteConfirmOpen(true)}
            size="small"
            sx={{ borderRadius: '10px' }}
            data-testid="card-detail-delete"
          >
            {t('Delete')}
          </Button>
        </Box>
      </Box>

      {/* Content Grid */}
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          {/* Tire Info */}
          <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              {t('tire_hotel.tire_info')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Chip label={t(`tire_hotel.${card.tire_set_type}`)} />
              {card.studded && <Chip label={t('tire_hotel.studded')} color="info" />}
              <Chip label={t(`tire_hotel.${card.rim_type}`)} variant="outlined" />
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">{t('tire_hotel.tire_brand')}</Typography>
                <Typography variant="body2" fontWeight={500}>{card.tire_brand || '-'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">{t('tire_hotel.tire_model')}</Typography>
                <Typography variant="body2" fontWeight={500}>{card.tire_model || '-'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3} data-testid="card-detail-tire-size">
                <Typography variant="caption" color="text.secondary">
                  {card.tire_size_rear ? t('tire_hotel.tire_size_front') : t('tire_hotel.tire_size')}
                </Typography>
                <Typography variant="body2" fontWeight={500}>{card.tire_size || '-'}</Typography>
              </Grid>
              {card.tire_size_rear && (
                <Grid item xs={6} sm={3} data-testid="card-detail-tire-size-rear">
                  <Typography variant="caption" color="text.secondary">{t('tire_hotel.tire_size_rear')}</Typography>
                  <Typography variant="body2" fontWeight={500}>{card.tire_size_rear}</Typography>
                </Grid>
              )}
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">{t('tire_hotel.dot_code')}</Typography>
                <Typography variant="body2" fontWeight={500}>{card.dot_code || '-'}</Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Tread Depths */}
          <Paper elevation={0} sx={{ p: 3, mb: 3 }} data-testid="card-detail-tread-depths">
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              {t('tire_hotel.tread_depth')}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, maxWidth: 400 }}>
              {['fl', 'fr', 'rl', 'rr'].map((pos) => {
                const value = card[`tread_depth_${pos}`];
                return (
                  <Paper key={pos} variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {t(`tire_hotel.tread_depth_${pos}`)}
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{ fontWeight: 600, color: getTreadColor(value) }}
                    >
                      {value != null ? `${value}` : '-'}
                    </Typography>
                    {value != null && (
                      <Typography variant="caption" color="text.secondary">mm</Typography>
                    )}
                  </Paper>
                );
              })}
            </Box>
          </Paper>

          {/* Notes */}
          {(card.condition_notes || card.service_notes || card.notes) && (
            <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                {t('tire_hotel.notes')}
              </Typography>
              {card.condition_notes && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">{t('tire_hotel.condition_notes')}</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{card.condition_notes}</Typography>
                </Box>
              )}
              {card.service_notes && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">{t('tire_hotel.service_notes')}</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{card.service_notes}</Typography>
                </Box>
              )}
              {card.notes && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">{t('tire_hotel.notes')}</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{card.notes}</Typography>
                </Box>
              )}
            </Paper>
          )}
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={4}>
          {/* Customer & Vehicle */}
          <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              {t('tire_hotel.customer_vehicle')}
            </Typography>
            <Box
              onClick={() => card.customer && setCustomerDialogOpen(true)}
              data-testid="card-detail-customer-link"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1.5,
                cursor: card.customer ? 'pointer' : 'default',
                '&:hover': { textDecoration: card.customer ? 'underline' : 'none' },
              }}
            >
              <PersonIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ color: card.customer ? 'secondary.main' : 'inherit' }}
              >
                {card.customer_name}
              </Typography>
            </Box>
            <Box
              onClick={() => card.vehicle && setVehicleDialogOpen(true)}
              data-testid="card-detail-vehicle-link"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: card.vehicle ? 'pointer' : 'default',
                '&:hover': { textDecoration: card.vehicle ? 'underline' : 'none' },
              }}
            >
              <DirectionsCarIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              <Typography
                variant="body2"
                fontWeight={500}
                sx={{ color: card.vehicle ? 'secondary.main' : 'inherit' }}
              >
                {card.vehicle_display || card.vehicle_registration_number}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
              {t('tire_hotel.customer_vehicle_edit_hint')}
            </Typography>
          </Paper>

          {/* Storage */}
          <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              {t('tire_hotel.storage_info')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('tire_hotel.location')}</Typography>
                <Typography variant="body2" fontWeight={500}>{card.location_label || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('tire_hotel.status')}</Typography>
                <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    label={t(`tire_hotel.${card.status}`)}
                    color={getStatusColor(card.status)}
                  />
                  <NeedsReplacementChip card={card} t={t} />
                </Box>
              </Box>
              {card.needs_replacement && (
                <Box data-testid="card-detail-replacement-reason">
                  <Typography variant="caption" color="text.secondary">{t('tire_hotel.replacement_reason')}</Typography>
                  <Typography variant="body2" fontWeight={500}>
                    {card.replacement_reason || '-'}
                    {card.replacement_flagged_at ? ` (${formatDate(card.replacement_flagged_at)?.split(' ')[0]})` : ''}
                  </Typography>
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary">{t('tire_hotel.storage_start_date')}</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {card.storage_start_date ? formatDate(card.storage_start_date)?.split(' ')[0] : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('tire_hotel.last_swap_date')}</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {card.last_swap_date ? formatDate(card.last_swap_date)?.split(' ')[0] : '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('tire_hotel.contract_price')}</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {card.contract_price != null ? `${card.contract_price} \u20AC` : '-'}
                </Typography>
              </Box>
              {card.insurance_included && (
                <Chip size="small" label={t('tire_hotel.insurance_included')} color="info" variant="outlined" />
              )}
            </Box>
          </Paper>

          {/* Photos */}
          <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                {t('tire_hotel.photos')}
              </Typography>
              <ImagePickerButton
                onChange={handlePhotoUpload}
                accept="image/*"
                disabled={uploading}
                buttonLabel={t('tire_hotel.upload_photo')}
                buttonProps={{
                  size: 'small',
                  startIcon: uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />,
                }}
              />
            </Box>
            {card.photos && card.photos.length > 0 ? (
              <ImageList cols={2} gap={8}>
                {card.photos.map((photo) => (
                  <ImageListItem key={photo.id} sx={{ cursor: 'pointer' }} onClick={() => setLightboxPhoto(photo)}>
                    <img
                      src={photo.image}
                      alt={photo.description || ''}
                      loading="lazy"
                      style={{ borderRadius: 8, objectFit: 'cover', height: 120 }}
                    />
                    <ImageListItemBar
                      sx={{ borderRadius: '0 0 8px 8px' }}
                      actionIcon={
                        <IconButton
                          sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handlePhotoDelete(photo.id); }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      }
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                {t('tire_hotel.no_photos')}
              </Typography>
            )}
          </Paper>

          {/* History */}
          <Paper elevation={0} sx={{ p: 3 }} data-testid="card-detail-history">
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              {t('tire_hotel.history')}
            </Typography>
            {card.history && card.history.length > 0 ? (
              <List dense disablePadding>
                {card.history.map((entry) => (
                  <ListItem key={entry.id} disablePadding sx={{ mb: 1.5 }}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {historyActionLabel(entry.action)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(entry.timestamp)} {entry.performed_by_name ? `- ${entry.performed_by_name}` : ''}
                          </Typography>
                          {entry.notes && (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                              {entry.notes}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                {t('tire_hotel.no_history')}
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Condition Report Dialog */}
      <ConditionReportDialog
        open={conditionReportOpen}
        onClose={() => setConditionReportOpen(false)}
        card={card}
        onSent={() => {
          setSuccessSnackbar(t('tire_hotel.report_sent_success'));
          fetchCard();
        }}
      />

      {/* Edit Dialog */}
      <TireHotelCardForm
        t={t}
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        card={card}
        onSaved={handleFormSaved}
      />

      {/* Customer and vehicle records behind this card */}
      <CustomerDetailsDialog
        open={customerDialogOpen}
        onClose={() => setCustomerDialogOpen(false)}
        customerId={card.customer}
        onCustomerUpdated={fetchCard}
      />
      <VehicleDetailsDialog
        open={vehicleDialogOpen}
        onClose={() => setVehicleDialogOpen(false)}
        vehicleId={card.vehicle}
        onVehicleUpdated={fetchCard}
      />

      {/* Flag / clear "Uusittava" */}
      <Dialog
        open={flagDialogOpen}
        onClose={() => !flagSaving && setFlagDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        data-testid="replacement-flag-dialog"
      >
        <DialogTitle>
          {t(card.needs_replacement ? 'tire_hotel.clear_needs_replacement' : 'tire_hotel.mark_needs_replacement')}
        </DialogTitle>
        <DialogContent>
          {card.needs_replacement && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('tire_hotel.clear_needs_replacement_help')}
            </Typography>
          )}
          <TextField
            autoFocus
            fullWidth
            size="small"
            label={t(card.needs_replacement ? 'tire_hotel.clear_needs_replacement_notes' : 'tire_hotel.replacement_reason')}
            placeholder={card.needs_replacement ? '' : t('tire_hotel.replacement_reason_placeholder')}
            value={flagReason}
            onChange={(e) => setFlagReason(e.target.value)}
            error={!!flagError}
            helperText={flagError || ' '}
            inputProps={{ maxLength: 255, 'data-testid': 'replacement-flag-reason' }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFlagDialogOpen(false)} disabled={flagSaving}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            color={card.needs_replacement ? 'success' : 'warning'}
            onClick={handleSaveFlag}
            disabled={flagSaving}
            data-testid="replacement-flag-save"
          >
            {t('Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>{t('tire_hotel.delete_card')}</DialogTitle>
        <DialogContent>
          <Typography>{t('tire_hotel.confirm_delete')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            {t('Cancel')}
          </Button>
          <Button variant="contained" color="error" onClick={handleDelete}>
            {t('Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TireHotelCardDetail;
